import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, User, Coins } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

declare global {
  interface Window {
    Pi: any;
  }
}

const PiAppPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [quoteCount, setQuoteCount] = useState(0);
  const [currentQuote, setCurrentQuote] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sample quotes/insults for the app
  const quotes = [
    "You're not stupid; you just have bad luck thinking.",
    "I'd agree with you, but then we'd both be wrong.",
    "You're like a cloud. When you disappear, it's a beautiful day.",
    "If I wanted to kill myself, I'd climb your ego and jump to your IQ.",
    "You bring everyone so much joy when you leave the room.",
    "I'm not saying you're stupid, but you have bad luck when thinking.",
    "You're proof that evolution can go in reverse.",
    "I'd explain it to you, but I don't have any crayons with me.",
    "You're like Monday mornings - nobody likes you.",
    "If brains were dynamite, you wouldn't have enough to blow your nose."
  ];

  useEffect(() => {
    // Initialize Pi SDK when component mounts
    if (window.Pi) {
      initializePi();
    } else {
      console.error('Pi SDK not loaded');
    }
  }, []);

  const initializePi = async () => {
    try {
      // Pi SDK should already be initialized by index.html
      if (!window.Pi || !window.Pi._initialized) {
        throw new Error('Pi SDK not initialized');
      }
      
      // Authenticate user
      const scopes = ['payments', 'username'];
      
      const auth = await window.Pi.authenticate(scopes, onIncompletePaymentFound);
      
      setIsAuthenticated(true);
      setUsername(auth.user.username);
      setAccessToken(auth.accessToken);
      
      console.log('Pi authentication successful:', auth);
    } catch (error) {
      console.error('Pi authentication failed:', error);
    }
  };

  const onIncompletePaymentFound = (payment: any) => {
    console.log('Incomplete payment found:', payment);
    // Handle incomplete payments
    completePayment(payment.identifier, payment.transaction.txid);
  };

  const getRandomQuote = () => {
    if (quoteCount >= 3 && !showPaymentModal) {
      setShowPaymentModal(true);
      return;
    }

    const randomIndex = Math.floor(Math.random() * quotes.length);
    const quote = quotes[randomIndex];
    setCurrentQuote(quote);
    setQuoteCount(prev => prev + 1);
  };

  const createPayment = async () => {
    if (!window.Pi) {
      alert('Pi SDK not available');
      return;
    }

    setIsLoading(true);

    const paymentData = {
      amount: 0.01,
      memo: "This buys you three more insults! What a deal!!!",
      metadata: { 
        insultid: Date.now(),
        username: username 
      }
    };

    const paymentCallbacks = {
      onReadyForServerApproval: (paymentId: string) => {
        console.log('Payment ready for approval:', paymentId);
        approvePayment(paymentId);
      },
      onReadyForServerCompletion: (paymentId: string, txid: string) => {
        console.log('Payment ready for completion:', paymentId, txid);
        completePayment(paymentId, txid);
      },
      onCancel: (paymentId: string) => {
        console.log('Payment cancelled:', paymentId);
        setIsLoading(false);
        setShowPaymentModal(false);
      },
      onError: (error: any, payment: any) => {
        console.error('Payment error:', error);
        setIsLoading(false);
        alert('Payment failed: ' + error.message);
      }
    };

    try {
      await window.Pi.createPayment(paymentData, paymentCallbacks);
    } catch (error) {
      console.error('Failed to create payment:', error);
      setIsLoading(false);
      alert('Failed to create payment');
    }
  };

  const approvePayment = async (paymentId: string) => {
    try {
      const response = await fetch('/api/payment/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Key huod9cajmituiv44dxfhk8kh6cmgwezsfjd4syumaharz3tdtzlskkwmedtumdvc`
        },
        body: JSON.stringify({
          paymentId: paymentId,
          accessToken: accessToken
        })
      });

      const result = await response.text();
      console.log('Payment approval result:', result);
    } catch (error) {
      console.error('Payment approval failed:', error);
    }
  };

  const completePayment = async (paymentId: string, txid: string) => {
    try {
      const response = await fetch('/api/payment/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Key huod9cajmituiv44dxfhk8kh6cmgwezsfjd4syumaharz3tdtzlskkwmedtumdvc`
        },
        body: JSON.stringify({
          paymentId: paymentId,
          txid: txid
        })
      });

      const result = await response.text();
      console.log('Payment completion result:', result);
      
      // Payment successful - reset counter and close modal
      setQuoteCount(0);
      setShowPaymentModal(false);
      setIsLoading(false);
      
      alert('Payment successful! You can now get more insults! 🎉');
    } catch (error) {
      console.error('Payment completion failed:', error);
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="text-6xl mb-4">🥧</div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            Insult-o-meter
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Connecting to Pi Network...
          </p>
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Insult-o-meter</h1>
      </div>

      <div className="px-4 pb-8">
        {/* User Info */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <User size={24} className="text-purple-600 dark:text-purple-400" />
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                  Welcome, {username}!
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Connected to Pi Network
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Coins size={20} className="text-yellow-500" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                π 0.01 per 3 insults
              </span>
            </div>
          </div>
        </Card>

        {/* Quote Display */}
        <Card className="p-6 mb-6 text-center min-h-[200px] flex items-center justify-center">
          {currentQuote ? (
            <div>
              <div className="text-4xl mb-4">😈</div>
              <p className="text-lg text-gray-800 dark:text-gray-100 font-medium leading-relaxed">
                "{currentQuote}"
              </p>
            </div>
          ) : (
            <div>
              <div className="text-4xl mb-4">🎯</div>
              <p className="text-gray-600 dark:text-gray-300">
                Click the button below to get your first creative insult!
              </p>
            </div>
          )}
        </Card>

        {/* Quote Counter */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-300">Insults Used:</span>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i <= quoteCount 
                        ? 'bg-purple-600' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {quoteCount}/3
              </span>
            </div>
          </div>
        </Card>

        {/* Get Quote Button */}
        <Button
          onClick={getRandomQuote}
          variant="gradient"
          size="lg"
          className="w-full mb-4"
        >
          <Zap size={20} className="mr-2" />
          Gimme an Insult!
        </Button>

        {/* App Info */}
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700">
          <div className="text-center">
            <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
              How it Works
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Get 3 free creative insults, then pay π 0.01 for 3 more! 
              All payments are processed securely through Pi Network.
            </p>
          </div>
        </Card>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md p-6">
            <div className="text-center">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                Want More Insults?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                You've used your 3 free insults! Pay π 0.01 to get 3 more creative insults.
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowPaymentModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createPayment}
                  variant="gradient"
                  className="flex-1"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    'Processing...'
                  ) : (
                    <>
                      <Coins size={16} className="mr-1" />
                      Pay π 0.01
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PiAppPage;